// The CV module's per-frame trace → data for the plots on /demo/full-metrics.
//
// A trace is columns, not rows: {frame: [...], t_ms: [...], rep_id: [...],
// <value>: [...]} (lgp_cv_module docs/client_report_mode.md, "Per-frame
// trace"). A column's kind comes from its first non-null value:
// number → a plot, boolean → a "check passed / failed" lane, string → a lane of
// categories (feedback). null is a frame without a value: a gap in the line, not a zero.

// The recharts SVG plot can't handle more points than this: a long trace is thinned.
export const MAX_POINTS = 2000;
export const TRACE_VERSION = 1;

const BOOKKEEPING = ['frame', 't_ms', 'rep_id'];

// A value next to its threshold. No columns, no plot. A group of thresholds only
// is what the settings set rather than what was measured (drawn as steps).
// id is stable: plotHelp.json is keyed by it.
// zeroLine draws a horizontal line at 0; referenceY, one at that value.
export const GROUPS = [
  { id: 'lip_stretch', title: 'Растяжение губ', values: ['stretch_left', 'stretch_right'], thresholds: ['stretch_needed'] },
  // mouth opening: "Fence", "Open your mouth" (required and minimum) and "Smile" (maximum: mouth closed).
  // In "Fence" it is the gap between the inner lips (kp62–kp66), hence its own title.
  { id: 'mouth_open', title: 'Открытие рта', titleByExercise: { 'show-teeth': 'Размыкание губ' }, values: ['mouth_open'],
    thresholds: ['mouth_open_needed', 'mouth_open_min', 'mouth_open_max'] },
  { id: 'teeth_visibility', title: 'Видимость зубов', values: ['teeth_lip_area_ratio'], thresholds: ['teeth_lip_area_ratio_min'] },
  { id: 'teeth_clench', title: 'Сжатие зубов', values: ['teeth_gap_ratio'], thresholds: ['teeth_gap_ratio_max'] },
  // "Open your mouth"
  { id: 'mouth_not_too_wide', title: 'Рот не слишком широкий', values: ['mouth_stretch'], thresholds: ['mouth_stretch_max'] },
  // "Smile"
  { id: 'smile_angle', title: 'Угол улыбки', values: ['smile_angle'], thresholds: ['smile_angle_needed'] },
  // the left and right parts (up to the nose base) add up to the width; they are not checked themselves
  { id: 'mouth_width', title: 'Ширина рта', values: ['mouth_width', 'mouth_width_left', 'mouth_width_right'],
    thresholds: ['mouth_width_needed'] },
  // derived on the client (faceAsymmetry.js), only on /demo/metrics-face-asymmetry: each PATIENT side's
  // share of the width (patient left = mouth_width_right, see faceAsymmetry.js)
  { id: 'mouth_width_share', title: 'Ширина рта: доли левой и правой стороны',
    values: ['width_share_patient_left', 'width_share_patient_right'], thresholds: [], referenceY: 0.5 },
  // the corners above the line through the upper lip midpoint, parallel to the eyes; "smile" needs both > 0
  { id: 'corner_height', title: 'Высота уголков губ', values: ['corner_height_left', 'corner_height_right'],
    thresholds: ['corner_height_min'] },
  // not roundness: the outer lip height to the width. "Smile": above the maximum gives the "smile" hint;
  // "Kiss": must be above the minimum (lips pursed into a tube)
  // "Kiss" also has left and right halves (the lip height on its side to its half of the width)
  { id: 'mouth_round', title: 'Высота рта к ширине', values: ['mouth_round', 'mouth_round_left', 'mouth_round_right'],
    thresholds: ['mouth_round_max', 'mouth_round_min'] },
  // "Fence": each corner (kp48 image-left, kp54 image-right) above the mouth centre (midpoint of kp62/kp66)
  // along the face's vertical axis, over the inner-eye distance; + raised, 0 level, - drooping
  { id: 'corner_lift', title: 'Подъём уголков губ', values: ['corner_lift_left', 'corner_lift_right'],
    thresholds: [], zeroLine: true },
  // "Kiss": the gap between the lip masks to the nose length; null means the lip masks were not found
  { id: 'lips_gap', title: 'Щель между губами', values: ['lips_gap'], thresholds: ['lips_gap_max', 'lips_gap_open_max'] },
  // "Kiss" with asymmetry_probe: the lip midpoints off the face axis, over the inner-eye distance;
  // + is image right in the raw (unmirrored) video, i.e. toward the patient's left ("L" on the video)
  { id: 'lip_midline_shift', title: 'Смещение середины губ от оси лица', values: ['upper_lip_shift', 'lower_lip_shift'],
    thresholds: [], zeroLine: true },
  // "Surprised"
  { id: 'brows', title: 'Брови', values: ['brow_left', 'brow_right'], thresholds: ['brows_needed'] },
  { id: 'eyes_open', title: 'Глаза открыты', values: ['eye_left', 'eye_right'], thresholds: ['eyes_open_min'] },
  { id: 'personalization_thresholds', title: 'Пороги персонализации', values: [],
    thresholds: ['open_lips_thresh', 'mouth_stretch_threshold'] },
  // hold_done_ms: how long the pose has been held so far (grows from 0 to hold_ms, back to 0 when the pose breaks).
  // Old traces don't have it: then only the threshold is drawn.
  { id: 'hold', title: 'Удержание', values: ['hold_done_ms'], thresholds: ['hold_ms'], thresholdsAlone: true },
];

const THRESHOLDS = new Set(GROUPS.flatMap((g) => g.thresholds));

// Groups that go first for an exercise, in this order; ids not on the plot are skipped.
// "Smile": the mouth width (or its derived share plot on the asymmetry page), then the corner height.
export const GROUP_ORDER_BY_EXERCISE = {
  'smiley-face': ['mouth_width_share', 'mouth_width', 'corner_height'],
};

/** The exercise's priority groups first, then the rest in their usual order (a stable reorder). */
export function orderGroups(groups, exercise) {
  const first = (exercise && GROUP_ORDER_BY_EXERCISE[exercise]) || [];
  if (first.length === 0) return groups;
  const rank = (g) => { const i = first.indexOf(g.id); return i === -1 ? first.length : i; };
  return groups.map((g, i) => [g, i]).sort(([a, i], [b, j]) => rank(a) - rank(b) || i - j).map(([g]) => g);
}

// States, not checks: "no" here is not a failure.
const STATES = new Set(['repetition_in_progress', 'teeth_or_tongue_visible', 'face_still', 'lips_found']);

export const LABELS = {
  stretch_left: 'левый угол',
  stretch_right: 'правый угол',
  stretch_needed: 'нужно',
  mouth_open: 'открытие',
  mouth_open_needed: 'нужно',
  teeth_lip_area_ratio: 'зубы / губы',
  teeth_lip_area_ratio_min: 'минимум',
  teeth_gap_ratio: 'щель между зубами',
  teeth_gap_ratio_max: 'максимум',
  hold_ms: 'нужно, мс',
  hold_done_ms: 'удержано, мс',
  open_lips_thresh: 'порог открытия',
  mouth_stretch_threshold: 'порог растяжения',
  feedback: 'подсказка',
  repetition_in_progress: 'идёт повтор',
  tongue_inside_ok: 'язык во рту',
  teeth_clenched_ok: 'зубы сжаты',
  mouth_stretched_ok: 'губы растянуты',
  mouth_open_ok: 'рот открыт',
  teeth_visible_ok: 'зубы видны',
  mouth_open_min: 'минимум',
  mouth_open_max: 'максимум (рот закрыт)',
  mouth_stretch: 'ширина к калибровке',
  mouth_stretch_max: 'максимум',
  mouth_not_too_wide_ok: 'рот не слишком широкий',
  teeth_or_tongue_visible: 'видны зубы или язык',
  mouth_closed_ok: 'рот закрыт',
  mouth_round: 'высота / ширина',
  mouth_round_max: 'максимум',
  mouth_round_ok: 'рот не слишком высокий',
  mouth_round_min: 'минимум',
  mouth_round_left: 'левая половина',
  mouth_round_right: 'правая половина',
  mouth_round_enough_ok: 'губы вытянуты',
  face_still: 'нет движения',
  lips_found: 'губы найдены',
  lips_gap: 'щель / нос',
  lips_gap_max: 'губы сомкнуты ниже',
  lips_gap_open_max: 'рот открыт выше',
  lips_closed_ok: 'губы сомкнуты',
  mouth_not_open_ok: 'рот не открыт',
  upper_lip_shift: 'середина верхней губы (kp51)',
  lower_lip_shift: 'середина нижней губы (kp57)',
  smile_angle: 'угол уголков, °',
  smile_angle_needed: 'нужно',
  smile_ok: 'улыбка',
  mouth_width: 'ширина рта',
  mouth_width_needed: 'нужно',
  mouth_width_left: 'левая часть',
  mouth_width_right: 'правая часть',
  width_share_patient_left: 'доля L',
  width_share_patient_right: 'доля R',
  corner_height_left: 'левый уголок',
  corner_height_right: 'правый уголок',
  corner_height_min: 'минимум',
  corner_lift_left: 'уголок слева на видео (kp48)',
  corner_lift_right: 'уголок справа на видео (kp54)',
  brow_left: 'левая бровь',
  brow_right: 'правая бровь',
  brows_needed: 'нужно',
  brow_left_ok: 'левая бровь поднята',
  brow_right_ok: 'правая бровь поднята',
  eye_left: 'левый глаз',
  eye_right: 'правый глаз',
  eyes_open_min: 'минимум',
  eyes_open_ok: 'глаза открыты',
};

export const label = (name) => LABELS[name] || name;

const seconds = (ms) => Math.round(ms) / 1000;

/**
 * null if the trace can be drawn; otherwise, why not. /server accepts any JSON
 * object, and the page must not crash on another version or a module error.
 */
export function traceProblem(trace) {
  if (!trace || trace.trace_version !== TRACE_VERSION) return 'Формат trace не поддерживается';
  const columns = trace.columns;
  if (!columns || typeof columns !== 'object') return 'В trace нет колонок';
  const n = columns.t_ms?.length;
  for (const name of Object.keys(columns)) {
    if (!Array.isArray(columns[name]) || columns[name].length !== n) return `Колонка «${name}» повреждена`;
  }
  if (!BOOKKEEPING.every((name) => Array.isArray(columns[name]))) return 'В trace нет времени кадров';
  return null;
}

function kindOf(values) {
  const first = values.find((v) => v !== null && v !== undefined);
  if (typeof first === 'number') return 'number';
  if (typeof first === 'boolean') return 'check';
  if (typeof first === 'string') return 'category';
  return null;
}

const namesOf = (columns, kind) =>
  Object.keys(columns).filter((name) => !BOOKKEEPING.includes(name) && kindOf(columns[name]) === kind);

function extreme(values, from, to, pick) {
  let best = null;
  for (let i = from; i < to; i += 1) {
    const v = values[i];
    if (v !== null && v !== undefined && (best === null || pick(v, best))) best = v;
  }
  return best;
}

/**
 * Rows for recharts: {t (s), frame, rep_id, <number>…}.
 * Up to MAX_POINTS frames, all of them. Beyond that the frames are cut into
 * buckets with two rows each: the minimum of the measured values at the start
 * of the bucket and the maximum at its end, so a short spike or dip is not
 * lost. Thresholds are taken as they are on those frames, plus a row on every
 * frame where a threshold changes, so the step is not late.
 */
export function chartRows(columns) {
  const n = columns.t_ms.length;
  const numeric = namesOf(columns, 'number');
  const measured = numeric.filter((name) => !THRESHOLDS.has(name));
  const thresholds = numeric.filter((name) => THRESHOLDS.has(name));
  const frameRow = (i) => {
    const row = { t: seconds(columns.t_ms[i]), frame: columns.frame[i], rep_id: columns.rep_id[i] };
    for (const name of numeric) row[name] = columns[name][i] ?? null;
    return row;
  };
  if (n <= MAX_POINTS) return { rows: [...Array(n).keys()].map(frameRow), step: 1 };

  const step = Math.ceil((n * 2) / MAX_POINTS);
  const rows = [];
  for (let start = 0; start < n; start += step) {
    const end = Math.min(start + step, n);
    const low = frameRow(start);
    const high = frameRow(end - 1);
    for (const name of measured) {
      low[name] = extreme(columns[name], start, end, (v, best) => v < best);
      high[name] = extreme(columns[name], start, end, (v, best) => v > best);
    }
    rows.push(low);
    for (let i = start + 1; i < end - 1; i += 1) {
      if (thresholds.some((name) => columns[name][i] !== columns[name][i - 1])) rows.push(frameRow(i));
    }
    if (end - 1 > start) rows.push(high);
  }
  return { rows, step };
}

/**
 * Plots: the known "value + threshold" groups, then each unknown number on its own (id = `col:<column>`).
 * exercise (optional, the trace's exercise slug) picks a group's titleByExercise override and
 * the GROUP_ORDER_BY_EXERCISE order.
 */
export function chartGroups(columns, exercise) {
  const numeric = new Set(namesOf(columns, 'number'));
  const groups = [];
  for (const g of GROUPS) {
    const values = g.values.filter((name) => numeric.has(name));
    const thresholds = g.thresholds.filter((name) => numeric.has(name));
    if (values.length === 0 && thresholds.length === 0) continue;
    if (values.length === 0 && g.values.length > 0 && !g.thresholdsAlone) continue; // a threshold without its value has nothing to show
    groups.push({
      id: g.id, title: (exercise && g.titleByExercise?.[exercise]) || g.title, values, thresholds, zeroLine: Boolean(g.zeroLine),
      ...(g.referenceY !== undefined && { referenceY: g.referenceY }),
    });
    [...g.values, ...g.thresholds].forEach((name) => numeric.delete(name));
  }
  // `col:` keeps an unknown column from picking up a group's help text or React key.
  for (const name of numeric) groups.push({ id: `col:${name}`, title: name, values: [name], thresholds: [], zeroLine: false });
  return orderGroups(groups, exercise);
}

/** Lanes: categories, states and checks as runs of equal values, over every frame. */
export function lanes(columns) {
  const t = columns.t_ms.map(seconds);
  const make = (name, kind) => {
    const values = columns[name];
    const runs = [];
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i] ?? null;
      const last = runs[runs.length - 1];
      if (last && last.value === value) continue;
      if (last) last.to = t[i];
      runs.push({ from: t[i], to: t[i], value });
    }
    if (runs.length) runs[runs.length - 1].to = t[t.length - 1];
    return { name, kind, runs };
  };
  const booleans = namesOf(columns, 'check');
  return [
    ...namesOf(columns, 'category').map((name) => make(name, 'category')),
    ...booleans.filter((name) => STATES.has(name)).map((name) => make(name, 'state')),
    ...booleans.filter((name) => !STATES.has(name)).map((name) => make(name, 'check')),
  ];
}

/** Repetition starts: the frames where rep_id changes. */
export function repStarts(columns) {
  const starts = [];
  for (let i = 1; i < columns.rep_id.length; i += 1) {
    if (columns.rep_id[i] !== columns.rep_id[i - 1]) starts.push({ t: seconds(columns.t_ms[i]), repId: columns.rep_id[i] });
  }
  return starts;
}
